//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@mui/material';
import { JobDetail } from '@/redux/slices/slice_types';
import { StatusColors } from '@/constants/constants';
import { selectStages, selectStatuses } from '@/redux/selectors/constantsSelectors';
import { useAppSelector } from '@/redux/hooks';
import PolarisIcon from './PolarisIcon';

type StatusDisplayProps = {
  status: string;
  className?: string;
  stage?: string;
  reason: JobDetail['status_reason'];
};

const StatusDisplay = ({ stage, status, className, reason }: StatusDisplayProps) => {
  const stages = useAppSelector(selectStages);
  const statuses = useAppSelector(selectStatuses);
  const stageLabel = stages[stage as keyof typeof stages] || stage?.toUpperCase();

  const label = useMemo(() => {
    if (stage) {
      return stageLabel;
    } else if (status && statuses[status]) {
      return statuses[status];
    } else {
      return status;
    }
  }, [stage, stageLabel, stages, statuses, status]);

  if (!status) return null;

  const content = (
    <Status className={className} color={StatusColors[status as keyof typeof StatusColors]} sx={{ opacity: status === 'cancelled' ? 0.5 : 1 }}>
      {label}
      {status === 'cancelled' && reason && <PolarisIcon icon="note" height={16} style={{ display: 'block' }} />}
      {reason && status === 'failed' && (
        <Tooltip title={reason} placement="bottom-end" arrow>
          {<PolarisIcon icon="error" height={16} />}
        </Tooltip>
      )}
      {status === 'cancelling' && (
        <Tooltip title="Cancelling... Please wait for job to complete cancellation" placement="bottom-end" arrow>
          <PolarisIcon icon="loading" height={16} />
        </Tooltip>
      )}
    </Status>
  );

  return reason && status === 'cancelled' ? (
    <Tooltip title={reason} placement="bottom-end" arrow slotProps={{ tooltip: { sx: { maxWidth: 'max(22vw, 300px)' } } }}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default StatusDisplay;

type StatusProps = {
  color: string;
};
const Status = styled(Typography)<StatusProps>`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(0.5)};
  justify-content: center;
  align-items: center;
  white-space: nowrap;

  text-transform: uppercase;
  letter-spacing: 0.75px;
  background: ${({ theme }) => theme.palette.background.default};
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  padding: ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  border: 2px solid transparent;
  text-align: center;
  background-color: ${({ theme, color }) => {
    switch (color) {
      case 'error':
        return `${theme.palette.error.main}2a`;
      case 'success':
        return `${theme.palette.success.main}2a`;
      case 'info':
        return `${theme.palette.info.main}2a`;
      case 'default':
        return theme.palette.background.default;
      case 'warning':
      default:
        return `${theme.palette.warning.main}2a`;
    }
  }};

  color: ${({ theme, color }) => {
    switch (color) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'info':
        return theme.palette.info.main;
      case 'default':
        return theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[500];
      case 'warning':
      default:
        return theme.palette.warning.main;
    }
  }};

  border-color: ${({ theme, color }) => {
    switch (color) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'info':
        return theme.palette.info.main;
      case 'default':
        return theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[500];
      case 'warning':
      default:
        return theme.palette.warning.main;
    }
  }};
`;
